import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Pagination } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type {
    JournalSourceFilter,
    JournalStatusFilter,
} from '@/api/handlers/resources/journal/requests';
import { JournalEntryModal } from '@/pages/accounting/journalEntry';
import { JournalEntryDrawer, JournalTable } from './components';
import {
    JOURNAL_SOURCE_FILTERS,
    JOURNAL_STATUS_FILTERS,
    getPaginationWindow,
} from './journalMeta';
import type { JournalPageModel } from './useJournalPage';

type JournalPageViewProps = {
    model: JournalPageModel;
};

export const JournalPageView: React.FC<JournalPageViewProps> = ({ model }) => {
    const { t } = useTranslation('pages');
    const pageWindow = getPaginationWindow(model.totalPages, model.page);

    return (
        <div
            className="flex w-full flex-col gap-5"
            {...getNodeTestIdProp('journal-section')}
        >
            {/* Toolbar: status filter + count + New entry. No date range — the
                Books shell owns the window for every tab. */}
            <div className="flex flex-wrap items-center gap-3">
                <Segment
                    aria-label={t('journal.filters.statusAria')}
                    onSelectionChange={(key) =>
                        model.onStatusChange(key as JournalStatusFilter)
                    }
                    selectedKey={model.status}
                    size="sm"
                    {...getNodeTestIdProp('journal-status-filter')}
                >
                    {JOURNAL_STATUS_FILTERS.map((filter) => (
                        <Segment.Item
                            id={filter.id}
                            key={filter.id}
                            {...getNodeTestIdProp(
                                `journal-filter-${filter.id}`,
                            )}
                        >
                            <Segment.Separator />
                            {t(filter.labelKey)}
                        </Segment.Item>
                    ))}
                </Segment>

                <Segment
                    aria-label={t('journal.filters.sourceAria')}
                    onSelectionChange={(key) =>
                        model.onSourceChange(key as JournalSourceFilter)
                    }
                    selectedKey={model.source}
                    size="sm"
                    {...getNodeTestIdProp('journal-source-filter')}
                >
                    {JOURNAL_SOURCE_FILTERS.map((filter) => (
                        <Segment.Item
                            id={filter.id}
                            key={filter.id}
                            {...getNodeTestIdProp(
                                `journal-source-filter-item-${filter.id}`,
                            )}
                        >
                            <Segment.Separator />
                            {t(filter.labelKey)}
                        </Segment.Item>
                    ))}
                </Segment>

                <div className="ml-auto flex items-center gap-4">
                    <span
                        className="text-sm text-muted"
                        {...getNodeTestIdProp('journal-entries-count')}
                    >
                        {t('journal.entriesCount', { count: model.total })}
                    </span>
                    <Button
                        onPress={model.onNewEntry}
                        variant="primary"
                        {...getNodeTestIdProp('journal-new-entry')}
                    >
                        <Icon aria-hidden icon="gravity-ui:plus" />
                        {t('journal.newEntry')}
                    </Button>
                </div>
            </div>

            {/* Account drill-in filter chip */}
            {model.accountId ? (
                <div
                    className="flex items-center gap-2 self-start rounded-lg bg-surface-secondary px-3 py-1.5 text-sm"
                    {...getNodeTestIdProp('journal-account-filter')}
                >
                    <Icon
                        aria-hidden
                        className="size-4 text-muted"
                        icon="gravity-ui:funnel"
                    />
                    <span className="text-muted">
                        {t('journal.accountFilter')}
                    </span>
                    <span className="font-medium text-foreground">
                        {model.filterAccountLabel}
                    </span>
                    <Button
                        aria-label={t('journal.clearAccountFilter')}
                        isIconOnly
                        onPress={model.onClearAccountFilter}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp('journal-clear-account-filter')}
                    >
                        <Icon aria-hidden icon="gravity-ui:xmark" />
                    </Button>
                </div>
            ) : null}

            {/* Table / loading — a load error is thrown to the page's
                ErrorBoundary, so it is never rendered here. */}
            {model.isLoading && model.entries.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 text-muted">
                    <Icon
                        aria-hidden
                        className="size-7 animate-spin text-default-500"
                        icon="gravity-ui:arrows-rotate-right"
                    />
                    <span className="text-sm">{t('journal.loading')}</span>
                </div>
            ) : (
                <JournalTable
                    entries={model.entries}
                    fromDisplay={isoToDisplayDate(model.fromDate)}
                    onWiden={model.onWiden}
                    toDisplay={isoToDisplayDate(model.toDate)}
                    onSelect={model.onSelectEntry}
                />
            )}

            {/* Pagination */}
            {model.totalPages > 1 ? (
                <Pagination size="sm">
                    <Pagination.Content>
                        <Pagination.Item>
                            <Pagination.Previous
                                aria-label={t('journal.pagination.previous')}
                                isDisabled={model.page === 1}
                                onPress={() =>
                                    model.onPageChange(model.page - 1)
                                }
                            >
                                <Pagination.PreviousIcon />
                            </Pagination.Previous>
                        </Pagination.Item>
                        {pageWindow.map((entry, index) =>
                            entry === 'ellipsis' ? (
                                <Pagination.Item
                                    key={`journal-page-ellipsis-${index}`}
                                >
                                    <Pagination.Ellipsis />
                                </Pagination.Item>
                            ) : (
                                <Pagination.Item key={entry}>
                                    <Pagination.Link
                                        isActive={entry === model.page}
                                        onPress={() =>
                                            model.onPageChange(entry)
                                        }
                                        {...getNodeTestIdProp(
                                            `journal-page-${entry}`,
                                        )}
                                    >
                                        {entry}
                                    </Pagination.Link>
                                </Pagination.Item>
                            ),
                        )}
                        <Pagination.Item>
                            <Pagination.Next
                                aria-label={t('journal.pagination.next')}
                                isDisabled={model.page === model.totalPages}
                                onPress={() =>
                                    model.onPageChange(model.page + 1)
                                }
                            >
                                <Pagination.NextIcon />
                            </Pagination.Next>
                        </Pagination.Item>
                    </Pagination.Content>
                </Pagination>
            ) : null}

            {/* New / edit modal */}
            <JournalEntryModal
                entry={model.modalEntry}
                isOpen={model.isModalOpen}
                onClose={model.onCloseModal}
                onSaved={model.onSaved}
            />

            {/* Detail drawer */}
            <JournalEntryDrawer
                entryId={model.selectedId}
                locale={model.locale}
                onClose={model.onCloseDrawer}
                onEdit={model.onEditEntry}
                onOpenEntry={model.onSelectEntry}
                onReversed={model.onCloseDrawer}
            />
        </div>
    );
};
